import { QueryResult, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import IMember from 'types/Member';

export interface IAuthenticationQueryData {
  members: IMember[];
}
export interface IAuthenticationQueryVariables {
  memberId: IMember['id'];
}

export type IAuthenticationQueryResult = QueryResult<IAuthenticationQueryData, IAuthenticationQueryVariables>;

export const AUTHENTICATION_QUERY = gql`
  query Authentication($memberId: Uuid) {
    members(filter: { id: { equal: $memberId } }) {
      archivedOn
      canSurvey
      certifiedEmail
      createdOn
      deletedOn
      email
      emailVerificationRequired
      firstName
      gustoEmployeeId
      id
      imageId
      imageUrl
      lastAccess
      lastName
      memberGroupId
      memberNumber
      memberSettings
      organizationId
      pendingEmail
      phone
      positionId
      submittedOn
      timeLocationRequired
      updatedOn
      username
      usernameUnique
      verifiedEmail
      memberGroup {
        id
        groupName
        deletedOn
      }
      organization {
        authoritativeSignature
        betaTester
        createdOn
        defaultBreakDuration
        defaultEndTime
        defaultStartTime
        deletedOn
        demo
        disclaimer
        id
        imageId
        inRecoveryMode
        gustoCompanyId
        lockOnSelfSignature
        minimumAppVersion
        organizationSettings
        organizationInfo {
          id
          organizationId
          longitude
          latitude
          address1
          address2
          city
          county
          state
          postalCode
          country
          phone
          trade
          currencyCode
          employeeMin
          employeeMax
          createdOn
          submittedOn
          updatedOn
          deletedOn
        }
        organizationPayPeriod {
          id
          organizationId
          payPeriodType
          startDate
          dayOne
          dayTwo
          createdOn
          submittedOn
          updatedOn
          deletedOn
        }
        organizationOvertimePeriods {
          id
          organizationId
          startTime
          lengthInDays
          weeklyOvertime
          dailyOvertime
          dailyDoubletime
          seventhDayOvertime
          seventhDayDoubletime
          saturdayMultiplier
          sundayMultiplier
          createdOn
          submittedOn
          updatedOn
          deletedOn
        }
        organizationName
        overtimeFlag
        ownedBy
        performanceScores
        requireCostCode
        requireProject
        revokedOn
        safetySignature
        safetySignatureMessage
        signatureDate
        stripeAccountCreatedOn
        stripeAccountStatus
        stripeAccountType
        stripeCustomerId
        submittedOn
        breakPolicy
        breakPolicyCalifornia
        breakPolicyMessage
        timeAccuracy
        timeAccuracyMessage
        timeRounding
        timeRoundingInterval
        timeRoundingType
        totalTimeFlag
        trackBudgets
        trackBudgetCost
        trackBudgetEquipmentCosts
        trackBudgetEquipmentHours
        trackBudgetProgress
        trackBudgetProgressValue
        trackCostCode
        trackEquipment
        trackPaidBreak
        trackProject
        updatedOn
        useProjectCostCodeScoping
        webGps
        busySignInQuestion
        hideUnassignedSubprojects
        timeOffRequests
        bulletin

        subscriptionStatus {
          annualSubscriptionCount
          annualSubscriptionDate
          annualSubscriptionInterval
          annualSubscriptionPendingInvoiceId
          cardLastFour
          cardType
          couponCode
          createdOn
          customerId
          deletedOn
          exempt
          hasTrialed
          id
          nextAssessmentAt
          organizationId
          periodDay
          pricePointAction
          pricePointHandle
          productHandle
          referralCode
          status
          submittedOn
          subscribedThroughProduct
          subscriptionId
          subscriptionUpdatedAt
          trialEndedAt
          updatedOn
          userCanceled
        }
      }
      position {
        canEditPosition
        createdOn
        dailyProjectPrompts
        dailyProjectPromptsUseSubprojects
        deletedOn
        id
        level
        manageBudgets
        viewBudgets
        manageCompanySettings
        manageCostCodes
        manageEmployeeGroups
        manageEmployees
        manageEquipment
        manageMediaCollection
        manageProjectGroups
        manageProjects
        manageTimeEntries
        memberLock
        manageSchedule
        viewSchedule
        clockInRestriction
        clockInRestrictionStartTime
        organizationId
        requestSignatures
        scopeByGroup
        submittedOn
        timeEvents
        title
        updatedOn
        viewAllActivityReport
        viewEquipmentDetails
        viewEquipmentStatus
        viewMemberActivityReport
        viewMemberExpenses
        viewProjectExpenses
        canManageProjectCostCodeQuantity
        manageBulletinPosts
      }
    }
  }
`;

export const useAuthenticationQuery = (variables: IAuthenticationQueryVariables): IAuthenticationQueryResult =>
  useQuery<IAuthenticationQueryData, IAuthenticationQueryVariables>(AUTHENTICATION_QUERY, { variables });

export default AUTHENTICATION_QUERY;
